import { BLUE_100, BLUE_600, GRAY_300, GRAY_400, GRAY_700, GRAY_800, GRAY_900, WHITE } from 'constants/styling/theme'
import { BorderAllOutlined, ChairOutlined, CleaningServicesOutlined, FormatPaintOutlined, HelpOutlineOutlined, SyncOutlined } from '@mui/icons-material'
import { Box, Stack, Typography } from '@mui/material'

import { GenerationModeSelectorProps } from 'models/visualStaging/virtualStagingTypes'
import { GenerationType } from 'constants/visualStaging/visualStagingTypes'
import { MUITooltip } from 'components/common/MUITooltip'
import React from 'react'
import { useTranslation } from 'react-i18next'

/**
 * GenerationModeSelector component for Virtual Staging.
 * Matches Figma design with Furnitures and Surfaces sections.
 * Allows users to select between "Stage an empty room" and "Restyle a room".
 * 
 * @example
 * <GenerationModeSelector
 *   generationType={virtualStagingState.generationType}
 *   onGenerationTypeChange={changeGenerationType}
 *   isDisabled={virtualStagingState.isGeneratingStaging}
 * />
 */
export const GenerationModeSelector: React.FC<GenerationModeSelectorProps> = ({
  generationType,
  onGenerationTypeChange,
  isDisabled = false,
}) => {
  const { t } = useTranslation(['photo_studio_editing', 'visual_staging'])

  const handleSelect = (type: GenerationType) => {
    if (!isDisabled) {
      onGenerationTypeChange(type)
    }
  }

  const isSelected = (type: GenerationType) => generationType === type

  const tooltipContent = (
    <Stack gap={1} sx={{ padding: '0.4rem 0.8rem' }}>
      <Typography variant="text-sm" fontWeight={600}>
        {t('virtual_staging.generation_mode.tooltip.how_it_works_title')}
      </Typography>
      <Typography variant="text-sm">
        {t('virtual_staging.generation_mode.tooltip.how_it_works_description')}
      </Typography>
      <Typography variant="text-sm" fontWeight={600} sx={{ marginTop: '2rem' }}>
        {t('virtual_staging.generation_mode.tooltip.stage_empty_room_title')}
      </Typography>
      <Typography variant="text-sm">
        {t('virtual_staging.generation_mode.tooltip.stage_empty_room_description')}
      </Typography>
      <Typography variant="text-sm" fontWeight={600} sx={{ marginTop: '2rem' }}>
        {t('virtual_staging.generation_mode.tooltip.restyle_room_title')}
      </Typography>
      <Typography variant="text-sm">
        {t('virtual_staging.generation_mode.tooltip.restyle_room_description')}
      </Typography>
      <Typography variant="text-sm" fontWeight={600} sx={{ marginTop: '2rem' }}>
        {t('virtual_staging.generation_mode.tooltip.fine_tune_title')}
      </Typography>
      <Typography variant="text-sm" sx={{ marginLeft: '1.2rem', paddingLeft: '0.4rem' }}>
        • {t('virtual_staging.generation_mode.tooltip.preserve_zone')}
      </Typography>
      <Typography variant="text-sm" sx={{ marginLeft: '1.2rem', paddingLeft: '0.4rem' }}>
        • {t('virtual_staging.generation_mode.tooltip.replace_items')}
      </Typography>
      <Typography variant="text-sm" fontWeight={600} sx={{ marginTop: '2rem' }}>
        {t('virtual_staging.generation_mode.tooltip.room_type_selector_title')}
      </Typography>
      <Typography variant="text-sm">
        {t('virtual_staging.generation_mode.tooltip.room_type_selector_description')}
      </Typography>
    </Stack>
  )

  return (
    <Stack gap={1.6}>
      {/* How AI staging works - with tooltip */}
      <MUITooltip
        id="ai-staging-tooltip"
        enterDelay={300}
        content={tooltipContent}
        placement="bottom-start"
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={0.4}
          sx={{ cursor: 'help', width: 'fit-content' }}
        >
          <Typography
            variant="text-xs"
            sx={{
              fontWeight: 550,
              color: GRAY_900,
              lineHeight: 1.5,
              letterSpacing: '-0.48px',
            }}
          >
            {t('virtual_staging.how_ai_staging_works')}
          </Typography>
          <HelpOutlineOutlined
            sx={{
              fontSize: '1.2rem',
              color: GRAY_900,
            }}
          />
        </Stack>
      </MUITooltip>

      {/* Furnitures Section */}
      <Stack gap={0.6}>
        <Typography
          variant="text-xs"
          sx={{
            fontWeight: 450,
            color: GRAY_700,
            lineHeight: 1.5,
            letterSpacing: '-0.48px',
          }}
        >
          {t('virtual_staging.furnitures')}
        </Typography>
        <Stack gap={0.8}>
          {/* Declutter room */}
          <Box
            onClick={() => handleSelect(GenerationType.DECLUTTER_ROOM)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.8rem',
              padding: '1.2rem',
              borderRadius: '0.8rem',
              border: `1px solid ${isSelected(GenerationType.DECLUTTER_ROOM) ? BLUE_600 : GRAY_300}`,
              backgroundColor: isSelected(GenerationType.DECLUTTER_ROOM) ? BLUE_100 : WHITE,
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.6 : 1,
              transition: 'all 0.2s ease-in-out',
              '&:hover': !isDisabled && !isSelected(GenerationType.DECLUTTER_ROOM) ? {
                borderColor: GRAY_400,
              } : {},
            }}
          >
            <CleaningServicesOutlined
              sx={{
                fontSize: '1.6rem',
                color: GRAY_800,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                flex: 1,
                fontSize: '1.4rem',
                fontWeight: isSelected(GenerationType.DECLUTTER_ROOM) ? 500 : 400,
                color: isSelected(GenerationType.DECLUTTER_ROOM) ? GRAY_900 : GRAY_700,
                lineHeight: 1.4,
                letterSpacing: '-0.56px',
              }}
            >
              {t('virtual_staging.generation_mode.declutter_room')}
            </Typography>
          </Box>

          {/* Stage an empty room */}
          <Box
            onClick={() => handleSelect(GenerationType.EMPTY_ROOM)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.8rem',
              padding: '1.2rem',
              borderRadius: '0.8rem',
              border: `1px solid ${isSelected(GenerationType.EMPTY_ROOM) ? BLUE_600 : GRAY_300}`,
              backgroundColor: isSelected(GenerationType.EMPTY_ROOM) ? BLUE_100 : WHITE,
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.6 : 1,
              transition: 'all 0.2s ease-in-out',
              '&:hover': !isDisabled && !isSelected(GenerationType.EMPTY_ROOM) ? {
                borderColor: GRAY_400,
              } : {},
            }}
          >
            <ChairOutlined
              sx={{
                fontSize: '1.6rem',
                color: GRAY_800,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                flex: 1,
                fontSize: '1.4rem',
                fontWeight: isSelected(GenerationType.EMPTY_ROOM) ? 500 : 400,
                color: isSelected(GenerationType.EMPTY_ROOM) ? GRAY_900 : GRAY_700,
                lineHeight: 1.4,
                letterSpacing: '-0.56px',
              }}
            >
              {t('virtual_staging.generation_mode.empty_room')}
            </Typography>
          </Box>

          {/* Restyle a room */}
          <Box
            onClick={() => handleSelect(GenerationType.FURNISHED_ROOM)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.8rem',
              padding: '1.2rem',
              borderRadius: '0.8rem',
              border: `1px solid ${isSelected(GenerationType.FURNISHED_ROOM) ? BLUE_600 : GRAY_300}`,
              backgroundColor: isSelected(GenerationType.FURNISHED_ROOM) ? BLUE_100 : WHITE,
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.6 : 1,
              transition: 'all 0.2s ease-in-out',
              '&:hover': !isDisabled && !isSelected(GenerationType.FURNISHED_ROOM) ? {
                borderColor: GRAY_400,
              } : {},
            }}
          >
            <SyncOutlined
              sx={{
                fontSize: '1.6rem',
                color: GRAY_800,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                flex: 1,
                fontSize: '1.4rem',
                fontWeight: isSelected(GenerationType.FURNISHED_ROOM) ? 500 : 400,
                color: isSelected(GenerationType.FURNISHED_ROOM) ? GRAY_900 : GRAY_700,
                lineHeight: 1.4,
                letterSpacing: '-0.56px',
              }}
            >
              {t('virtual_staging.generation_mode.furnished_room')}
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {/* Surfaces Section */}
      <Stack gap={0.6}>
        <Typography
          variant="text-xs"
          sx={{
            fontWeight: 450,
            color: GRAY_700,
            lineHeight: 1.5,
            letterSpacing: '-0.48px',
          }}
        >
          {t('virtual_staging.surfaces')}
        </Typography>
        <Stack gap={0.8}>
          {/* Paint walls */}
          <Box
            onClick={() => handleSelect(GenerationType.PAINT_WALLS)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.8rem',
              padding: '1.2rem',
              borderRadius: '0.8rem',
              border: `1px solid ${isSelected(GenerationType.PAINT_WALLS) ? BLUE_600 : GRAY_300}`,
              backgroundColor: isSelected(GenerationType.PAINT_WALLS) ? BLUE_100 : WHITE,
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.6 : 1,
              transition: 'all 0.2s ease-in-out',
              '&:hover': !isDisabled && !isSelected(GenerationType.PAINT_WALLS) ? {
                borderColor: GRAY_400,
              } : {},
            }}
          >
            <FormatPaintOutlined
              sx={{
                fontSize: '1.6rem',
                color: GRAY_800,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                flex: 1,
                fontSize: '1.4rem',
                fontWeight: isSelected(GenerationType.PAINT_WALLS) ? 500 : 400,
                color: isSelected(GenerationType.PAINT_WALLS) ? GRAY_900 : GRAY_700,
                lineHeight: 1.4,
                letterSpacing: '-0.56px',
              }}
            >
              {t('virtual_staging.surface_options.paint_walls')}
            </Typography>
          </Box>

          {/* Change floor */}
          <Box
            onClick={() => handleSelect(GenerationType.CHANGE_FLOOR)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.8rem',
              padding: '1.2rem',
              borderRadius: '0.8rem',
              border: `1px solid ${isSelected(GenerationType.CHANGE_FLOOR) ? BLUE_600 : GRAY_300}`,
              backgroundColor: isSelected(GenerationType.CHANGE_FLOOR) ? BLUE_100 : WHITE,
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.6 : 1,
              transition: 'all 0.2s ease-in-out',
              '&:hover': !isDisabled && !isSelected(GenerationType.CHANGE_FLOOR) ? {
                borderColor: GRAY_400,
              } : {},
            }}
          >
            <BorderAllOutlined
              sx={{
                fontSize: '1.6rem',
                color: GRAY_800,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                flex: 1,
                fontSize: '1.4rem',
                fontWeight: isSelected(GenerationType.CHANGE_FLOOR) ? 500 : 400,
                color: isSelected(GenerationType.CHANGE_FLOOR) ? GRAY_900 : GRAY_700,
                lineHeight: 1.4,
                letterSpacing: '-0.56px',
              }}
            >
              {t('virtual_staging.surface_options.change_floor')}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </Stack>
  )
}
